import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

type ContactTab = 'customers' | 'suppliers';

interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
}

interface Supplier {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  tpin?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const ContactsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ContactTab>('customers');
  const [searchTerm, setSearchTerm] = useState('');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);

  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  const [supplierForm, setSupplierForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    tpin: '',
  });

  const resetForms = () => {
    setCustomerForm({ name: '', email: '', phone: '', address: '' });
    setSupplierForm({ name: '', email: '', phone: '', address: '', tpin: '' });
  };

  const { data: customersData, isLoading: isLoadingCustomers } = useQuery<{ customers: Customer[] }>({
    queryKey: ['contacts', 'customers'],
    queryFn: async () => {
      console.log('🔍 Fetching customers from API...');
      try {
        const res = await api.get('/api/contacts');
        console.log('👥 Contacts API response:', res.data);
        return { customers: res.data?.filter((c: any) => c.type === 'customer') || [] };
      } catch (error) {
        console.error('❌ Customers fetch error:', error);
        throw error;
      }
    },
  });

  const { data: suppliersData, isLoading: isLoadingSuppliers } = useQuery<{ suppliers: Supplier[] }>({
    queryKey: ['contacts', 'suppliers'],
    queryFn: async () => {
      const res = await api.get('/api/contacts');
      return { suppliers: res.data?.filter((c: any) => c.type === 'supplier') || [] };
    },
  });

  const customers = customersData?.customers || [];
  const suppliers = suppliersData?.suppliers || [];

  const filteredCustomers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q)
    );
  }, [customers, searchTerm]);

  const filteredSuppliers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.phone || '').toLowerCase().includes(q) ||
      (s.tpin || '').toLowerCase().includes(q)
    );
  }, [suppliers, searchTerm]);

  const createCustomerMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: customerForm.name.trim(),
        email: customerForm.email.trim() || undefined,
        phone: customerForm.phone.trim() || undefined,
        address: customerForm.address.trim() || undefined,
      };
      console.log('🚀 Creating customer with payload:', payload);
      try {
        const res = await api.post('/api/contacts/customers', payload);
        console.log('✅ Customer creation response:', res.data);
        return res.data?.customer as Customer;
      } catch (error) {
        console.error('❌ Customer creation error:', error);
        throw error;
      }
    },
    onMutate: async () => {
      console.log('🔄 Starting optimistic update for customer');
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['contacts', 'customers'] });
      
      // Snapshot the previous value
      const previousCustomers = queryClient.getQueryData(['contacts', 'customers']);
      console.log('📸 Previous customers snapshot:', previousCustomers);
      
      // Optimistically update to the new value
      const optimisticCustomer = {
        id: Date.now().toString(),
        name: customerForm.name.trim(),
        email: customerForm.email.trim() || undefined,
        phone: customerForm.phone.trim() || undefined,
        address: customerForm.address.trim() || undefined,
        type: 'customer' as const,
        isActive: true,
        totalPurchases: 0,
        lastPurchase: null,
        createdAt: new Date().toISOString(),
      };
      
      console.log('⚡ Adding optimistic customer:', optimisticCustomer);
      
      queryClient.setQueryData(['contacts', 'customers'], (old: any) => {
        const currentCustomers = old?.data?.customers || [];
        const newCustomers = {
          ...old,
          data: {
            customers: [...currentCustomers, optimisticCustomer]
          }
        };
        console.log('📝 Updated customers query data:', newCustomers);
        return newCustomers;
      });
      
      return { previousCustomers };
    },
    onError: (_err, _variables, context) => {
      console.error('💥 Customer creation failed, rolling back');
      // If the mutation fails, use the context returned from onMutate
      if (context?.previousCustomers) {
        console.log('🔄 Restoring previous customers');
        queryClient.setQueryData(['contacts', 'customers'], context.previousCustomers);
      }
      toast.error('Failed to create customer');
    },
    onSuccess: (data) => {
      console.log('🎉 Customer creation successful:', data);
      setIsCreateOpen(false);
      resetForms();
      toast.success('Customer created successfully');
    },
    onSettled: () => {
      console.log('🏁 Customer creation settled, invalidating cache');
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['contacts', 'customers'] });
    },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: async () => {
      if (!editingCustomer) return;
      const payload = {
        name: customerForm.name.trim() || undefined,
        email: customerForm.email.trim() || undefined,
        phone: customerForm.phone.trim() || undefined,
        address: customerForm.address.trim() || undefined,
      };
      const res = await api.put(`/api/contacts/customers/${editingCustomer.id}`, payload);
      return res.data?.customer as Customer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'customers'] });
      setIsEditOpen(false);
      setEditingCustomer(null);
      resetForms();
      toast.success('Customer updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update customer');
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/contacts/customers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'customers'] });
      toast.success('Customer deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete customer');
    },
  });

  const createSupplierMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: supplierForm.name.trim(),
        email: supplierForm.email.trim() || undefined,
        phone: supplierForm.phone.trim() || undefined,
        address: supplierForm.address.trim() || undefined,
        tpin: supplierForm.tpin.trim() || undefined,
      };
      const res = await api.post('/api/contacts/suppliers', payload);
      return res.data?.supplier as Supplier;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'suppliers'] });
      setIsCreateOpen(false);
      resetForms();
      toast.success('Supplier created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create supplier');
    },
  });

  const updateSupplierMutation = useMutation({
    mutationFn: async () => {
      if (!editingSupplier) return;
      const payload = {
        name: supplierForm.name.trim() || undefined,
        email: supplierForm.email.trim() || undefined,
        phone: supplierForm.phone.trim() || undefined,
        address: supplierForm.address.trim() || undefined,
        tpin: supplierForm.tpin.trim() || undefined,
      };
      const res = await api.put(`/api/contacts/suppliers/${editingSupplier.id}`, payload);
      return res.data?.supplier as Supplier;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'suppliers'] });
      setIsEditOpen(false);
      setEditingSupplier(null);
      resetForms();
      toast.success('Supplier updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update supplier');
    },
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/contacts/suppliers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'suppliers'] });
      toast.success('Supplier deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete supplier');
    },
  });

  const openCreate = () => {
    resetForms();
    setIsCreateOpen(true);
    setIsEditOpen(false);
    setEditingCustomer(null);
    setEditingSupplier(null);
  };

  const openEditCustomer = (c: Customer) => {
    setEditingCustomer(c);
    setEditingSupplier(null);
    setCustomerForm({
      name: c.name || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address || '',
    });
    setIsEditOpen(true);
    setIsCreateOpen(false);
  };

  const openEditSupplier = (s: Supplier) => {
    setEditingSupplier(s);
    setEditingCustomer(null);
    setSupplierForm({
      name: s.name || '',
      email: s.email || '',
      phone: s.phone || '',
      address: s.address || '',
      tpin: s.tpin || '',
    });
    setIsEditOpen(true);
    setIsCreateOpen(false);
  };

  const closeModals = () => {
    setIsCreateOpen(false);
    setIsEditOpen(false);
    setEditingCustomer(null);
    setEditingSupplier(null);
    resetForms();
  };

  const isLoading = activeTab === 'customers' ? isLoadingCustomers : isLoadingSuppliers;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="mt-1 text-sm text-gray-600">Manage customers and suppliers</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by name, phone, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="h-4 w-4 text-gray-400" />}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('customers')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${
              activeTab === 'customers' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'
            }`}
          >
            Customers
          </button>
          <button
            onClick={() => setActiveTab('suppliers')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${
              activeTab === 'suppliers' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'
            }`}
          >
            Suppliers
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">
              {activeTab === 'customers' ? 'Customers' : 'Suppliers'}
            </h2>
          </div>
          <div className="text-sm text-gray-600">
            Total: {activeTab === 'customers' ? filteredCustomers.length : filteredSuppliers.length}
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-sm text-gray-600">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    {activeTab === 'suppliers' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">TPIN</th>
                    )}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeTab === 'customers' && (
                    filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-6 text-sm text-gray-500 text-center">
                          No customers found
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((c) => (
                        <tr key={c.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{c.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{c.email || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{c.phone || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                            <div className="inline-flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openEditCustomer(c)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteCustomerMutation.mutate(c.id)}
                                loading={deleteCustomerMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )
                  )}

                  {activeTab === 'suppliers' && (
                    filteredSuppliers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-6 text-sm text-gray-500 text-center">
                          No suppliers found
                        </td>
                      </tr>
                    ) : (
                      filteredSuppliers.map((s) => (
                        <tr key={s.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.email || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.phone || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.tpin || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                            <div className="inline-flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openEditSupplier(s)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteSupplierMutation.mutate(s.id)}
                                loading={deleteSupplierMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {(isCreateOpen || isEditOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {isCreateOpen ? 'Add' : 'Edit'} {activeTab === 'customers' ? 'Customer' : 'Supplier'}
              </h3>
              <button onClick={closeModals} className="text-gray-500 hover:text-gray-700">×</button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {activeTab === 'customers' && (
                <>
                  <Input
                    label="Name"
                    value={customerForm.name}
                    onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                    required
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={customerForm.email}
                    onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                  />
                  <Input
                    label="Phone"
                    value={customerForm.phone}
                    onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                  />
                  <Input
                    label="Address"
                    value={customerForm.address}
                    onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                  />
                </>
              )}

              {activeTab === 'suppliers' && (
                <>
                  <Input
                    label="Name"
                    value={supplierForm.name}
                    onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                    required
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={supplierForm.email}
                    onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                  />
                  <Input
                    label="Phone"
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                  />
                  <Input
                    label="Address"
                    value={supplierForm.address}
                    onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                  />
                  <Input
                    label="TPIN"
                    value={supplierForm.tpin}
                    onChange={(e) => setSupplierForm({ ...supplierForm, tpin: e.target.value })}
                  />
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={closeModals}>Cancel</Button>
              {isCreateOpen && activeTab === 'customers' && (
                <Button
                  onClick={() => createCustomerMutation.mutate()}
                  loading={createCustomerMutation.isPending}
                  disabled={!customerForm.name.trim()}
                >
                  Save
                </Button>
              )}
              {isCreateOpen && activeTab === 'suppliers' && (
                <Button
                  onClick={() => createSupplierMutation.mutate()}
                  loading={createSupplierMutation.isPending}
                  disabled={!supplierForm.name.trim()}
                >
                  Save
                </Button>
              )}
              {isEditOpen && activeTab === 'customers' && (
                <Button
                  onClick={() => updateCustomerMutation.mutate()}
                  loading={updateCustomerMutation.isPending}
                  disabled={!customerForm.name.trim()}
                >
                  Save
                </Button>
              )}
              {isEditOpen && activeTab === 'suppliers' && (
                <Button
                  onClick={() => updateSupplierMutation.mutate()}
                  loading={updateSupplierMutation.isPending}
                  disabled={!supplierForm.name.trim()}
                >
                  Save
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
